import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { primaryText } from '../../../styling/stylingHelper';
import { canAccessMenuChangeDashboard } from '../../../services/accessManagement/accessManagement';
import { useAuth } from '../../../services/authentication/authContext';
import { useMenuChangeStore } from '../../../stores/appMenuChangeStore';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { useAppUserDefaultStore, isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import MenuChangeTimeline from './MenuChangeTimeline';
import MenuChangeActions from './MenuChangeActions';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import { MenuChangeLinkTarget, resolveMenuChangeLink } from '../../../services/menuChange/menuChangeLinks';

/**
 * Child page of the Menu Change dashboard, reached at
 * /menus/change-dashboard/:restaurantId. Mirrors the OrderHistory parent→child
 * pattern: distinct sibling route, entity re-derived from the URL param + store,
 * hand-rolled ArrowLeft back header, scroll reset on mount.
 */
const MenuChangeTimelinePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { restaurantId = '' } = useParams<{ restaurantId: string }>();
  const { backendUser, accountSettings } = useAuth();
  const canManage = canAccessMenuChangeDashboard(backendUser, accountSettings);

  const dashboard = useMenuChangeStore((s) => s.dashboard);
  const restaurants = useRestaurantStore((s) => s.restaurants);
  const loadRestaurants = useRestaurantStore((s) => s.loadAllData);
  const selectedRestaurant = useAppUserDefaultStore((s) => s.restaurant);
  const defaultsLoaded = useAppUserDefaultStore(s => s.isLoaded);
  const setRestaurant = useAppUserDefaultStore(s => s.setRestaurant);
  const loadDetail = useMenuChangeStore(s => s.loadDetail);
  const hasLinkTarget = ['rollout', 'week', 'task'].some(key => new URLSearchParams(search).has(key));
  const destination = `${restaurantId}${search}`;
  const [entry, setEntry] = useState<{ destination: string; target?: MenuChangeLinkTarget; error?: 'accessDenied' | 'unavailable' | 'loadFailed' } | null>(null);

  useEffect(() => {
    let current = true;
    void loadDetail(restaurantId, true).then(detail => {
      if (!current) return;
      if (!detail) {
        const status = useMenuChangeStore.getState().detailErrors[restaurantId];
        setEntry({ destination, error: status === 403 || status === 401 ? 'accessDenied' : status === 404 ? 'unavailable' : 'loadFailed' });
        return;
      }
      const target = hasLinkTarget ? resolveMenuChangeLink(detail, restaurantId, search) : null;
      setEntry({ destination, ...(hasLinkTarget && !target ? { error: 'unavailable' as const } : target ? { target } : {}) });
    });
    return () => { current = false; };
  }, [destination, restaurantId, search, hasLinkTarget, loadDetail]);

  useEffect(() => {
    if (!hasLinkTarget || !defaultsLoaded || entry?.destination !== destination || entry.error) return;
    const restaurant = restaurants.find(item => item.id === restaurantId);
    if (restaurant) setRestaurant(restaurant);
  }, [hasLinkTarget, defaultsLoaded, entry, destination, restaurants, restaurantId, setRestaurant]);

  useEffect(() => {
    loadRestaurants();
  }, [loadRestaurants]);

  // Restaurant name comes from the dashboard entry if we came from the grid;
  // deep-links (straight from the Menus page) have no dashboard loaded, so fall
  // back to the restaurant store.
  const restaurantName =
    dashboard.find((e) => e.restaurantId === restaurantId)?.restaurantName
    ?? restaurants.find((r) => r.id === restaurantId)?.name
    ?? '';

  // Same two-source fallback for the logo.
  const restaurantLogoUrl =
    dashboard.find((e) => e.restaurantId === restaurantId)?.restaurantLogoUrl
    ?? restaurants.find((r) => r.id === restaurantId)?.logoUrl
    ?? null;

  // Reset scroll to top on entering the detail page (orderhistory pattern).
  useEffect(() => {
    const scroller = document.getElementById('mainContainer') as HTMLDivElement | null;
    if (scroller) scroller.scrollTop = 0;
  }, []);

  // Back to the cross-restaurant grid only under "All restaurants" — with a
  // specific restaurant selected that overview isn't theirs to see, so we return
  // to the Menus page they came from.
  const handleGoBack = () =>
    navigate(isAllRestaurantsSelected(selectedRestaurant) ? '/menus/change-dashboard' : '/menus');

  if (entry?.destination !== destination) return <p className={primaryText} role="status">{t('common.loading')}</p>;
  if (entry.error) return <div className={cn(primaryText, 'space-y-3')}>
    <p role="alert">{t(`menuChange.links.${entry.error}`)}</p>
    <Link className="underline" to={entry.error === 'accessDenied' ? '/menus' : `/menus/change-dashboard/${restaurantId}`}>{t('menuChange.links.back')}</Link>
  </div>;

  return (
    <div>
      <MobilePageHeader title={t('menuChange.timelineHeader')} />
      <div className="flex items-center mb-4">
        <ArrowLeft className="w-6 h-6 mt-1 mr-2 cursor-pointer" onClick={handleGoBack} />
        <h1 className={cn('text-2xl font-semibold', primaryText, 'hidden lg:block')}>{t('menuChange.timelineHeader')}</h1>
      </div>

      <div className="flex justify-end mb-4"><MenuChangeActions key={restaurantId} restaurantId={restaurantId} /></div>
      <div className="bg-white dark:bg-background-dark rounded-xl border border-transparent dark:border-borderPrimary-dark p-4 sm:p-6">
        <MenuChangeTimeline key={destination} restaurantId={restaurantId} restaurantName={restaurantName} restaurantLogoUrl={restaurantLogoUrl} canManage={canManage} initialRolloutId={entry.target?.rollout.id} initialWeekPos={entry.target?.weekPos} focusTaskId={entry.target?.taskId} />
      </div>
    </div>
  );
};

export default MenuChangeTimelinePage;
